import { Button, InputField, CheckBoxInput } from "@/components";
import { useState, useCallback } from "react";
import { FaArrowRight } from "react-icons/fa6";
import { SheetClose } from "@/components/ui/sheet";
import { useDispatch } from "react-redux";
import { saveEmployeesLogin } from "../../../app/slices/employeesLoginSlice";
import { EmployeeLoginSchema } from "../../../schemas";
import { useValidation, useFieldError, useAlert } from "@/";
const EmployeeForm = ({ setSheetOpen = () => {} }) => {
  const { alert, AlertDialog } = useAlert();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    permissions: {
      "New Booking": false,
      "Booked Orders": false,
      Tracking: false,
      NDR: false,
      Reports: false,
      Support: false,
      Billing: false,
      Dashboard: true,
    },
  });
  const { validate, errors } = useValidation(EmployeeLoginSchema);
  const [fieldErrors, setFieldErrors] = useState([]);
  const getErrorMessage = useFieldError(fieldErrors);
  const [generalError, setGeneralError] = useState("");
  const dispatch = useDispatch();

  // Handle form submission
  const handleSubmit = async () => {
    setFieldErrors([]);
    setGeneralError("");

    const selectedPermissions = Object.keys(formData.permissions).filter(
      (key) => formData.permissions[key]
    );

    const body = {
      name: formData.name, // Fixing field names
      phone: formData.phone,
      email: formData.email,
      password: formData.password,
      permissions: selectedPermissions,
    };
    const result = validate(body);
    if (!result.isValid) {
      setFieldErrors(result.errors);
      return;
    }

    try {
      const resultAction = await dispatch(saveEmployeesLogin(result.data));

      if (saveEmployeesLogin.fulfilled.match(resultAction)) {
        console.log("Employee saved successfully:", resultAction.payload);
        await alert("Employee saved successfully", "success");
        setSheetOpen(false);
      } else {
        console.error("Failed to save employee:", resultAction.payload);
        alert("Failed to save employee", "error");
        setGeneralError(resultAction.payload);
      }
    } catch ({ error }) {
      console.error("Validation error:", error.message);
      alert(error.message, "error");
      setGeneralError(error);
    }
  };

  // Handle input changes
  const handleChange = useCallback((key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Handle checkbox change
  const handleCheckboxChange = useCallback((key) => {
    setFormData((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: !prev.permissions[key],
      },
    }));
  }, []);

  return (
    <div className="flex flex-col gap-2 mt-4">
      {generalError && (
        <p className="text-red-500 text-xs pl-2">{generalError}</p>
      )}
      <InputField
        label="Name"
        placeholder="Enter Name"
        id="name"
        type="text"
        value={formData.name}
        onChange={(val) => handleChange("name", val)}
        error={getErrorMessage("name")}
      />

      <InputField
        label="Email"
        placeholder="Enter Email"
        id="email"
        type="email"
        value={formData.email}
        onChange={(val) => handleChange("email", val)}
        error={getErrorMessage("email")}
      />

      <InputField
        label="Password"
        placeholder="Enter Password"
        id="password"
        type="password"
        value={formData.password}
        onChange={(val) => handleChange("password", val)}
        error={getErrorMessage("password")}
      />

      <InputField
        label="Phone"
        placeholder="Enter Phone Number"
        id="phone"
        type="text"
        value={formData.phone}
        onChange={(val) => handleChange("phone", val)}
        error={getErrorMessage("phone")}
      />

      {/* Permissions Section */}
      <span className="text-body2 font-ptsans text-secondary/60 pl-2">
        Permissions
      </span>
      <div className="grid grid-cols-2 gap-2 pl-4">
        {Object.keys(formData.permissions).map((key) => (
          <CheckBoxInput
            key={key}
            label={key}
            checked={key === "Dashboard" ? true : formData.permissions[key]}
            onChange={
              key === "Dashboard" ? () => {} : () => handleCheckboxChange(key)
            }
          />
        ))}
        {getErrorMessage("permissions") && (
          <p className="text-red-500 text-xs pl-2">
            {getErrorMessage("permissions")}
          </p>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <SheetClose asChild>
          <Button variant="outline" size="sm" className="mt-4">
            Close
          </Button>
        </SheetClose>

        <Button
          type="submit"
          onClick={handleSubmit}
          variant="default"
          icon={FaArrowRight}
          size="sm"
          className="mt-4"
        >
          Save
        </Button>
      </div>
      <AlertDialog />
    </div>
  );
};

export default EmployeeForm;
